// unitsStore.ts
import { create } from "zustand";
import type { Device } from "./devicesStore";
import type { CustomAlertProps } from "../../components/base/CustomAlert";
import { getError } from "../../utils/getError";
import { getAdminFetchGeneration } from "../../utils/adminFetchGeneration";
import { isAbortError } from "../../utils/api";
import type { TFunction } from "i18next";
import { type UseFormReset } from "react-hook-form";
import api from "../../utils/api";
import { useVehiclesSocketStore } from "./vehiclesSocketStore";
import { fetchDevicesFromAPI } from "../../hooks/useVehiclesSocketSubscription";

export interface Unit {
  id: string;
  name: string;
  icon?: string;
  /** Modelo de puntero del mapa (config/vehicleModels); vacío = por omisión. */
  pointer_model?: string;
  status?: { id: string; name: string };
  status_id?: string;
  plate?: string;
  brand?: string;
  model?: string;
  year?: number;
  type?: string;
  // Water types assigned to the vehicle from a truck yard (separate from the
  // unit `type`). Shown as badges in the units views.
  truck_yard_types?: string[];
  fuelType?: string;
  // Capacidad del tanque en litros (opcional): la bitácora la usa para litros y km/L.
  fuelCapacity?: number;
  remolque_id?: string;
  remolque?: {
    id: string;
    name?: string;
    plate: string;
  };
  policy_id?: string;
  policy_expiry_date?: string;
  createdAt: { _seconds: number; _nanoseconds: number };
  updatedAt: { _seconds: number; _nanoseconds: number };
  collections: string[];
  devices: Device[];
  lastAddress?: string;
  driver?: { id: string; name: string };
}

export interface UnitDto
  extends Omit<
    Unit,
    | "id"
    | "createdAt"
    | "updatedAt"
    | "collections"
    | "devices"
    | "lastAddress"
    | "driver"
  > {
  lang: string;
}

export interface UnitUpdateDto
  extends Omit<
    Unit,
    | "createdAt"
    | "updatedAt"
    | "collections"
    | "devices"
    | "lastAddress"
    | "driver"
  > {
  lang: string;
}

export interface UnitResponse
  extends Omit<Unit, "devices" | "lastAddress" | "driver"> {}

interface UnitsStore {
  units: Unit[];
  availableVehicles: Unit[];
  availableVehiclesLoading: boolean;
  devices: Device[];
  lastAddress?: string;
  driver?: { id: string; name: string };
  loading: boolean;
  error: string | null;
  meta?: {
    error: string | null;
    count: number;
    status: number;
    currentPage: number;
    totalPages: number;
    totalItems: number;
    url: string;
  };
  fetchUnits: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
  }) => Promise<void>;
  fetchAvailableVehicles: (params?: {
    limit?: number;
    page?: number;
    offset?: number;
  }) => Promise<void>;
  getUnitByPlate: (
    plate: string
  ) => Promise<{ success: boolean; data?: Unit; error?: string }>;
  updateUnitInStore: (unitId: string, updatedUnit: Partial<Unit>) => void;
  setUnits: (
    units: Unit[],
    meta?: {
      error: string | null;
      count: number;
      status: number;
      currentPage: number;
      totalPages: number;
      totalItems: number;
      url: string;
    }
  ) => void;
  setLoading: (loading: boolean) => void;
  connectOrDisconnectTrailerOnVehicle: (
    vehicleId: string,
    remolqueId: string
  ) => Promise<{ success: boolean; data?: any; error?: string }>;
  getUnitByVehicleId: (
    id: string
  ) => Promise<{ success: boolean; data?: Unit; error?: string }>;
  updateVehicleStatus: (
    vehicleId: string,
    statusId: string
  ) => Promise<{ success: boolean; data?: Unit; error?: string }>;
  deleteUnit: (
    unitId: string
  ) => Promise<{ success: boolean; error?: string }>;
}

export const useUnitsStore = create<UnitsStore>((set, get) => ({
  units: [],
  availableVehicles: [],
  availableVehiclesLoading: false,
  devices: [],
  lastAddress: undefined,
  driver: undefined,
  loading: false,
  error: null,
  meta: undefined,

  fetchUnits: async (params = {}) => {
    if (get().loading) return;
    const fetchGen = getAdminFetchGeneration();
    set({ loading: true, error: null });

    try {
      const { data } = await api.get("/vehicleList", {
        params: {
          limit: params.limit ?? 8,
          page: params.page ?? 1,
          ...params,
        },
      });
      if (getAdminFetchGeneration() !== fetchGen) return;
      
      const meta = data.meta || (data.next ? {
        currentPage: params.page ?? 1,
        totalPages: Math.ceil((data.total || data.data?.length || 0) / (params.limit ?? 8)),
        totalItems: data.total || data.data?.length || 0,
        count: data.data?.length || 0,
        status: 200,
        error: null,
        url: "/vehicleList",
      } : undefined);
      
      set({ units: data.data || [], meta, loading: false });
    } catch (err: any) {
      if (getAdminFetchGeneration() !== fetchGen || isAbortError(err)) return;
      set({ error: getError(err), loading: false });
    }
  },

  fetchAvailableVehicles: async (params = {}) => {
    if (get().availableVehiclesLoading) return;
    set({ availableVehiclesLoading: true });

    try {
      const { data } = await api.get("/vehicleListWithoutDeviceSelected", {
        params,
      });
      set({
        availableVehicles: data.data || [],
        availableVehiclesLoading: false,
      });
    } catch (err: any) {
      set({ availableVehiclesLoading: false });
    }
  },

  getUnitByVehicleId: async (id: string) => {
    try {
      const { data } = await api.get("/vehicleById", {
        params: { id },
      });
      const vehicle = data?.data?.data ?? data?.data ?? data;
      return { success: true, data: vehicle };
    } catch (err: any) {
      console.error("Error fetching vehicle by id:", err);
      const message = getError(err);
      return { success: false, error: message };
    }
  },

  getUnitByPlate: async (plate: string) => {
    try {
      const { data } = await api.get("/vehicle", {
        params: { plate },
        data: { plate },
      });
      const unit = data?.data?.data ?? data?.data ?? data;
      return { success: true, data: unit };
    } catch (err: any) {
      console.error("Error fetching unit by plate:", err);
      const message = getError(err);
      return { success: false, error: message };
    }
  },

  updateUnitInStore: (unitId: string, updatedUnit: Partial<Unit>) => {
    set((state) => ({
      units: state.units.map((unit) =>
        unit.id === unitId ? ({ ...unit, ...updatedUnit } as Unit) : unit
      ),
    }));
  },

  setUnits: (units: Unit[], meta) => {
    set({ units, meta });
  },

  setLoading: (loading: boolean) => set({ loading }),

  connectOrDisconnectTrailerOnVehicle: async (vehicleId: string, remolqueId: string) => {
    try {
      const { data } = await api.post("/associateOrRemoveRemolqueToVehicle", { vehicleId, remolqueId });

      if (data?.success || data?.data) {
        const updatedVehicle = data?.data;
        if (updatedVehicle) {
          get().updateUnitInStore(vehicleId, {
            remolque_id: updatedVehicle.remolque_id || null,
            remolque: updatedVehicle.remolque || null,
          });
        }
        return { success: true, data: updatedVehicle };
      }

      return { success: true, data: data };
    } catch (error) {
      console.error("Error connecting or disconnecting trailer on vehicle:", error);
      return { success: false, error: getError(error) };
    }
  },

  updateVehicleStatus: async (vehicleId: string, statusId: string) => {
    try {
      const { data } = await api.put(`/vehicleStatus?id=${vehicleId}`, {
        status_id: statusId,
      });

      const updatedUnit = data?.data?.data ?? data?.data ?? data;

      if (updatedUnit) {
        get().updateUnitInStore(vehicleId, { status_id: statusId });
      }

      return { success: true, data: updatedUnit };
    } catch (error) {
      console.error("Error updating vehicle status:", error);
      return { success: false, error: getError(error) };
    }
  },

  deleteUnit: async (unitId: string) => {
    try {
      await api.delete("/vehicle", { params: { id: unitId } });
      set((state) => ({
        units: state.units.filter((unit) => unit.id !== unitId),
      }));
      return { success: true };
    } catch (error) {
      console.error("Error deleting unit:", error);
      return { success: false, error: getError(error) };
    }
  },
}));

export const createUnit = async (
  unitData: UnitDto,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset: UseFormReset<UnitDto>
) => {
  try {
    await api.post("/vehicle", unitData);

    setLoadingSubmit(false);
    showAlert({
      title: t("units.form.success"),
      text: t("units.form.successMessage"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        reset();
      },
      showCancelButton: false,
    });
    fetchDevicesFromAPI(undefined, true).then((devices) => {
      if (devices) useVehiclesSocketStore.getState().refreshDevicesData(devices);
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("units.form.error"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const updateUnit = async (
  unitId: string,
  unitData: UnitDto,
  showAlert: (config: CustomAlertProps) => void,
  setLoadingSubmit: (loading: boolean) => void,
  t: TFunction<"translation", undefined>,
  onClose: () => void,
  reset: UseFormReset<UnitDto>
) => {
  try {
    const { data } = await api.put("/vehicle?id=" + unitId, { ...unitData });

    if (data.data) {
      useUnitsStore.getState().updateUnitInStore(unitId, data.data.data);
    }

    setLoadingSubmit(false);
    showAlert({
      title: t("units.form.updateSuccess"),
      text: t("units.form.updateSuccessMessage"),
      icon: "success",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {
        onClose();
        reset();
      },
      showCancelButton: false,
    });
    fetchDevicesFromAPI(undefined, true).then((devices) => {
      if (devices) useVehiclesSocketStore.getState().refreshDevicesData(devices);
    });
  } catch (error) {
    setLoadingSubmit(false);
    showAlert({
      title: t("units.form.updateError"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  }
};

export const getUnits = async (
  params: {
    limit?: number;
    page?: number;
    offset?: number;
    status?: string;
    type?: string;
    search?: string;
  },
  t: TFunction<"translation", undefined>,
  showAlert: (config: CustomAlertProps) => void
) => {
  // Limpiar error previo y activar loading
  useUnitsStore.setState({ loading: true, error: null });

  try {
    const { data } = await api.get("/vehicleList", { params });

    if (data.data) {
      // Usar data.meta si existe, si no construir meta desde data.next
      const meta = data.meta || (data.next ? {
        currentPage: params.page || 1,
        totalPages: data.next.page || 1,
        totalItems: data.data?.length || 0,
        count: data.data?.length || 0,
        status: 200,
        error: null,
        url: "/vehicleList",
      } : undefined);

      useUnitsStore.getState().setUnits(data.data, meta);
    }
  } catch (error) {
    // Ignorar errores de cancelación (logout, cambio de compañía, etc.)
    if (isAbortError(error)) return;

    useUnitsStore.setState({ error: getError(error, t) });
    showAlert({
      title: t("units.form.errorMessage"),
      text: getError(error, t),
      icon: "error",
      confirmButtonText: "OK",
      confirmButtonColor: "#2563eb",
      onConfirm: () => {},
    });
  } finally {
    useUnitsStore.getState().setLoading(false);
  }
};
